import { useMemo } from 'react';
import type { ApiResponseSnapshot } from '@/types';
import { extractIds, IdChip } from './IdChip';

export function ResponseViewer({
  response,
  loading,
  error,
}: {
  response: ApiResponseSnapshot | null;
  loading: boolean;
  error: string | null;
}) {
  const ids = useMemo(() => (response?.ok ? extractIds(response.body) : []), [response]);

  if (loading) {
    return (
      <div
        className="lp-rounded-2xl lp-p-6 lp-flex lp-items-center lp-gap-3 lp-shadow-card"
        style={{ background: '#ffffff', border: '1px solid #e5e7eb', color: '#64748b', borderRadius: 22 }}
      >
        <svg className="lp-w-4 lp-h-4 lp-animate-spin" style={{ color: '#6256ff' }} viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="lp-opacity-25" />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
        </svg>
        <span className="lp-text-sm">Sending request…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="lp-rounded-2xl lp-p-4 lp-text-sm lp-shadow-card"
        style={{ background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', borderRadius: 22 }}
      >
        <div className="lp-font-semibold lp-mb-1">Request failed</div>
        <div className="lp-font-mono lp-text-xs lp-whitespace-pre-wrap">{error}</div>
      </div>
    );
  }

  if (!response) {
    return (
      <div
        className="lp-rounded-2xl lp-p-8 lp-text-center lp-text-sm"
        style={{
          background: '#ffffff',
          border: '1px dashed #cbd5e1',
          color: '#94a3b8',
          borderRadius: 22,
        }}
      >
        Run the request to see the API response here.
      </div>
    );
  }

  return (
    <div className="lp-space-y-3">
      <div className="lp-surface-dark lp-overflow-hidden lp-shadow-card" style={{ borderRadius: 22 }}>
        <div
          className="lp-flex lp-items-center lp-gap-3 lp-px-4 lp-py-2.5"
          style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <span
            className="lp-text-[10px] lp-font-bold lp-px-2 lp-py-0.5 lp-rounded lp-tracking-wider"
            style={{
              background: response.ok ? '#a7f3d0' : '#fda4af',
              color: response.ok ? '#065f46' : '#9f1239',
            }}
          >
            {response.status || 'NETWORK'}
          </span>
          <span className="lp-text-[11px] lp-font-medium lp-tabular-nums" style={{ color: '#cbd5e1' }}>
            {response.durationMs.toFixed(0)} ms
          </span>
        </div>
        <pre
          className="lp-code-pane lp-text-[12.5px] lp-leading-relaxed lp-font-mono lp-p-4 lp-overflow-auto lp-max-h-[440px]"
          style={{ color: '#e2e8f0' }}
        >
          {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
        </pre>
      </div>

      {ids.length > 0 && (
        <div
          className="lp-px-4 lp-py-3 lp-flex lp-items-center lp-gap-2 lp-flex-wrap"
          style={{
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 16,
          }}
        >
          <span
            className="lp-text-[10px] lp-font-bold lp-uppercase lp-mr-1"
            style={{ letterSpacing: '0.12em', color: '#6256ff' }}
          >
            Captured ids
          </span>
          {ids.map((id) => (
            <IdChip key={id.field} label={id.field} value={id.value} />
          ))}
        </div>
      )}
    </div>
  );
}
